import React, { useState } from 'react';
import { Button, Row, Col } from 'antd';
import cx from 'classnames';

import {
  BOUserTypeEnum,
  FullBOUserResponseModel,
  BOUsersBankingInfoTypeEnum,
  EntityInformationResponseModel,
  ResourceDownloadResponse,
} from 'api/backofficeApiClient';
import {
  getBankingInfoTypeLabel,
  getBankingAccountTypeLabel,
} from 'utils/accountUtils';
import { getLocation } from 'utils/locationUtils';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import BasePageCard from 'components/wrappers/BasePageCard';
import BaseAlert from 'components/wrappers/BaseAlert/BaseAlert';
import { downloadFile, getFileNameWithoutExt } from 'utils/fileUtils';
import BaseSectionMessage from 'components/wrappers/BaseSectionMessage';
import { getRecruiterBankingInfoFile } from 'redux/reducers/recruiterSlice';
import { LoadRecruiterDetailsOptions } from 'features/recruiter/RecruiterDetailsPage/types';
import EditBankingInfoModal from 'features/account/PrimaryInfo/BankingInfo/EditBankingInfoModal';
import { useAppDispatch } from 'hooks/useAppDispatch';

import style from './BankingInfoSection.module.less';

type Props = {
  recruiterDetails: FullBOUserResponseModel;
  loadRecruiterDetails: (
    options?: LoadRecruiterDetailsOptions,
  ) => Promise<void>;
  entityInfo?: EntityInformationResponseModel;
  bankingInfoFile?: ResourceDownloadResponse;
};

export default function BankingInfoSection(props: Props) {
  const {
    recruiterDetails,
    loadRecruiterDetails,
    entityInfo,
    bankingInfoFile,
  } = props;
  const dispatch = useAppDispatch();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { boUserType, bankingInformation, companyId, userId } =
    recruiterDetails;

  const checkLocation = getLocation(
    bankingInformation?.mailMyChecksDetail?.address,
    bankingInformation?.mailMyChecksDetail?.city,
    bankingInformation?.mailMyChecksDetail?.state,
    bankingInformation?.mailMyChecksDetail?.zip,
  );

  const handleOpenEditModal = () => {
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
  };

  const handleDownloadBankingFile = async () => {
    const result = await dispatch(getRecruiterBankingInfoFile(userId));

    if (getRecruiterBankingInfoFile.fulfilled.match(result)) {
      try {
        await downloadFile({
          downloadUrl: result.payload.downloadUrl,
          fileName: result.payload.fileName,
        });
      } catch (e: any) {
        BaseAlert.error(e.message);
      }
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const onEditBankingInfo = async () => {
    await loadRecruiterDetails({ shouldLoadBankingFile: true });
  };

  return (
    <BasePageCard
      title="Banking information"
      className="stretch-card"
      icons={<span className="icon icon-banking"></span>}
      subTitle={
        <Button
          onClick={handleOpenEditModal}
          className="with-icon mobile-icon-only"
          size="large"
          icon={<span className="icon icon-edit" />}
        >
          Edit
        </Button>
      }
    >
      <div className="content-wrapper">
        {isEditModalOpen && (
          <EditBankingInfoModal
            companyId={companyId}
            bankingInfo={bankingInformation}
            entityInfo={entityInfo}
            visible={isEditModalOpen}
            onClose={handleCloseEditModal}
            onEdit={onEditBankingInfo}
            bankingFile={bankingInfoFile}
          />
        )}
        {!bankingInformation && (
          <BaseSectionMessage
            type="info"
            title=""
            message={
              <>
                {boUserType === BOUserTypeEnum.Company && (
                  <span>
                    Waiting for member to respond. Banking information will be
                    displayed after a member enters data.
                  </span>
                )}

                {boUserType === BOUserTypeEnum.SelfEmployed && (
                  <span>
                    Waiting for member to respond. Banking information will be
                    displayed after member enters data.
                  </span>
                )}

                {boUserType === BOUserTypeEnum.International && (
                  <span>
                    Waiting for member response. Banking information will be
                    displayed after member enters the data.
                  </span>
                )}
              </>
            }
          />
        )}
        {!!bankingInformation && (
          <>
            {bankingInformation.bankingInformationType ===
              BOUsersBankingInfoTypeEnum.DirectDeposit && (
              <>
                <Row gutter={[4, 4]} className="titled-sheet">
                  <Col xs={24}>
                    <div className="subtitle">Direct deposit</div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div className="title">Banking information type:</div>
                    <div>
                      {getBankingInfoTypeLabel(
                        bankingInformation.bankingInformationType,
                      )}
                    </div>
                  </Col>

                  {!!bankingInformation.directDepositDetail?.bankName && (
                    <Col xs={24} md={12}>
                      <div className="title">Bank name:</div>
                      <div>
                        {bankingInformation.directDepositDetail.bankName}
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.directDepositDetail?.holderName && (
                    <Col xs={24} md={12}>
                      <div className="title">Bank account holder name:</div>
                      <div>
                        {bankingInformation?.directDepositDetail.holderName}
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.directDepositDetail?.accountNumber && (
                    <Col xs={24} md={12}>
                      <div className="title">Account number:</div>
                      <div>
                        {bankingInformation.directDepositDetail.accountNumber}
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.directDepositDetail?.routingNumber && (
                    <Col xs={24} md={12}>
                      <div className="title">Routing number:</div>
                      <div>
                        {bankingInformation.directDepositDetail.routingNumber}
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.directDepositDetail?.accountType && (
                    <Col xs={24} md={12}>
                      <div className="title">Account type:</div>
                      <div>
                        {getBankingAccountTypeLabel(
                          bankingInformation.directDepositDetail.accountType,
                        )}
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.directDepositDetail?.ddEmailAddress && (
                    <Col xs={24} md={12}>
                      <div className="title">DD email address:</div>
                      <div>
                        {bankingInformation.directDepositDetail.ddEmailAddress}
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.directDepositDetail?.isConfirmation && (
                    <Col xs={24}>
                      <div className={style.wrapper}>
                        <span
                          className={cx(
                            'icon icon-check-outlined',
                            style.iconItem,
                          )}
                        />

                        {boUserType === BOUserTypeEnum.Company && (
                          <span>
                            Member authorized myBasePay, LLC to initiate
                            credit/debit in to the account listed above
                          </span>
                        )}

                        {(boUserType === BOUserTypeEnum.SelfEmployed ||
                          boUserType === BOUserTypeEnum.International) && (
                          <span>
                            Member authorized myBasePay, LLC to initiate
                            credit/debit for the account listed above
                          </span>
                        )}
                      </div>
                    </Col>
                  )}
                </Row>
              </>
            )}

            {bankingInformation.bankingInformationType ===
              BOUsersBankingInfoTypeEnum.MailMyChecks && (
              <>
                <Row gutter={[4, 4]} className="titled-sheet">
                  <Col xs={24}>
                    <div className="subtitle">Mail the checks</div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div className="title">Banking information type:</div>
                    <div>
                      {getBankingInfoTypeLabel(
                        bankingInformation.bankingInformationType,
                      )}
                    </div>
                  </Col>

                  {!!bankingInformation.mailMyChecksDetail?.payableTo && (
                    <Col xs={24} md={12}>
                      <div className="title">Payable to:</div>
                      <div>
                        {bankingInformation.mailMyChecksDetail.payableTo}
                      </div>
                    </Col>
                  )}

                  {!!checkLocation && (
                    <Col xs={24} md={12}>
                      <div className="title">Check address:</div>
                      <div>{checkLocation}</div>
                    </Col>
                  )}

                  {!!bankingInformation.mailMyChecksDetail?.isConfirmation && (
                    <Col xs={24}>
                      <div className={style.wrapper}>
                        <span
                          className={cx(
                            'icon icon-check-outlined',
                            style.iconItem,
                          )}
                        />

                        {(boUserType === BOUserTypeEnum.Company ||
                          boUserType === BOUserTypeEnum.SelfEmployed) && (
                          <span>
                            Member authorized myBasePay, LLC distribute ‘member
                            commissions’ in the form of a live check
                          </span>
                        )}

                        {boUserType === BOUserTypeEnum.International && (
                          <span>
                            Member authorized myBasePay, LLC to distribute
                            ‘member commissions’ in the form of a mailed check
                          </span>
                        )}
                      </div>
                    </Col>
                  )}
                </Row>
              </>
            )}

            {bankingInformation.bankingInformationType ===
              BOUsersBankingInfoTypeEnum.International && (
              <>
                <Row gutter={[4, 4]} className="titled-sheet">
                  <Col xs={24}>
                    <div className="subtitle">International</div>
                  </Col>

                  <Col xs={24} md={12}>
                    <div className="title">Banking information type:</div>
                    <div>
                      {getBankingInfoTypeLabel(
                        bankingInformation.bankingInformationType,
                      )}
                    </div>
                  </Col>

                  {!!bankingInformation.internationalDetail
                    ?.accountHolderName && (
                    <Col xs={24} md={12}>
                      <div className="title">Account holder name:</div>
                      <div>
                        {
                          bankingInformation.internationalDetail
                            .accountHolderName
                        }
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.internationalDetail
                    ?.internationalBankName && (
                    <Col xs={24} md={12}>
                      <div className="title">Bank name:</div>
                      <div>
                        {
                          bankingInformation.internationalDetail
                            .internationalBankName
                        }
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.internationalDetail
                    ?.internationalAccountNumber && (
                    <Col xs={24} md={12}>
                      <div className="title">Account number:</div>
                      <div>
                        {
                          bankingInformation.internationalDetail
                            .internationalAccountNumber
                        }
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.internationalDetail?.swiftCode && (
                    <Col xs={24} md={12}>
                      <div className="title">Swift code:</div>
                      <div>
                        {bankingInformation.internationalDetail.swiftCode}
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.internationalDetail
                    ?.internationalRoutingCode && (
                    <Col xs={24} md={12}>
                      <div className="title">International routing code:</div>
                      <div>
                        {
                          bankingInformation.internationalDetail
                            .internationalRoutingCode
                        }
                      </div>
                    </Col>
                  )}

                  {!!bankingInformation.internationalDetail?.confirmation1 &&
                    bankingInformation.internationalDetail?.confirmation2 && (
                      <Col xs={24}>
                        <div className={style.wrapper}>
                          <span
                            className={cx(
                              'icon icon-check-outlined',
                              style.iconItem,
                            )}
                          />

                          <span>
                            Member authorized myBasePay, LLC to wire currency
                            entries into the account listed above. This account
                            can accept American Currency Transactions
                          </span>
                        </div>
                      </Col>
                    )}
                </Row>
              </>
            )}
          </>
        )}
        {bankingInfoFile && (
          <>
            <div className="title">Download banking information:</div>
            <span
              className="link-imitation"
              onClick={handleDownloadBankingFile}
            >
              {getFileNameWithoutExt(bankingInfoFile?.fileName)}
            </span>
          </>
        )}
      </div>
    </BasePageCard>
  );
}
